import { useCallback, useEffect, useState } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import Meta from '../components/Meta';
import HomeFooter from '../components/HomeFooter';
import PageHeader from '../components/shell/PageHeader';
import ShowGrid from '../components/ShowGrid';
import { useAuth } from '../util/auth';
import { useParams, useRouter } from '../util/router';
import { getIsFollowing, getOrgPageData, toggleIsFollowing } from '../util/db';

function OrgPage() {
  const { id } = useParams();
  const auth = useAuth();
  const router = useRouter();
  const userId = auth.user?.id;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followPending, setFollowPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    getOrgPageData(id)
      .then(result => !cancelled && setData(result))
      .catch(err => {
        console.error(err);
        if (!cancelled) setError('We couldn’t find this venue.');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!userId) {
      setIsFollowing(false);
      return;
    }
    getIsFollowing(userId, id).then(setIsFollowing).catch(console.error);
  }, [userId, id]);

  const handleFollow = useCallback(async () => {
    if (!userId) {
      router.push(`/auth/signin?next=${encodeURIComponent(`/org/${id}`)}`);
      return;
    }
    setFollowPending(true);
    setIsFollowing(prev => !prev);
    try {
      await toggleIsFollowing(userId, id);
    } catch (err) {
      console.error(err);
      setIsFollowing(prev => !prev);
    } finally {
      setFollowPending(false);
    }
  }, [userId, id, router]);

  const org = data?.org;
  const venues = data?.venues ?? [];
  const neighborhoods = [...new Set(venues.map(v => v.neighborhood).filter(Boolean))];
  const location =
    venues.length === 1
      ? [venues[0].address1, venues[0].neighborhood].filter(Boolean).join(', ')
      : neighborhoods.join(' · ');

  const followButton = (
    <button
      type="button"
      onClick={handleFollow}
      disabled={followPending || !org}
      className={`rounded-xl px-5 py-2 text-sm font-bold transition-colors ${
        isFollowing
          ? 'bg-slate-300 text-slate-900 hover:bg-slate-400'
          : 'bg-black text-white hover:bg-slate-800'
      }`}
    >
      {isFollowing ? 'Unfollow' : 'Follow'}
    </button>
  );

  return (
    <>
      <Meta title={org?.org_name ? `${org.org_name} - Showrunner` : 'Showrunner'} />
      <PageHeader back title={org?.org_name ?? (error ? 'Venue' : ' ')} actions={followButton}>
        {(org?.bio || location) && (
          <div className="max-w-3xl space-y-2">
            {location && (
              <p className="flex items-start gap-1 text-sm font-semibold text-secondary-red">
                <MapPinIcon className="mt-0.5 w-4 shrink-0" />
                <span>
                  {location}
                  {venues.length > 1 && (
                    <span className="font-normal text-black/50"> ({venues.length} locations)</span>
                  )}
                </span>
              </p>
            )}
            {org?.bio && <p className="text-sm text-black/70 md:text-base">{org.bio}</p>}
          </div>
        )}
      </PageHeader>

      <section className="mx-auto min-h-[60vh] w-full max-w-[90rem] px-4 py-6 md:px-8 md:py-10">
        {error && <p className="py-16 text-center text-lg text-slate-600">{error}</p>}

        {!data && !error && (
          <div className="flex justify-center py-16">
            <span className="loading loading-spinner loading-lg text-secondary-red" />
          </div>
        )}

        {data && (
          <>
            <h2 className="mb-4 text-xl font-semibold capitalize text-slate-700 md:text-2xl">
              Live & upcoming shows
            </h2>
            {data.liveShows.length ? (
              <ShowGrid shows={data.liveShows} source="org" />
            ) : (
              <p className="py-8 text-center text-sm text-slate-400">No live or upcoming shows</p>
            )}

            <h2 className="mb-4 mt-14 text-xl font-semibold capitalize text-slate-700 md:text-2xl">
              Past shows
            </h2>
            {data.pastShows.length ? (
              <ShowGrid shows={data.pastShows} source="org" variant="past" />
            ) : (
              <p className="py-8 text-center text-sm text-slate-400">No past shows this year</p>
            )}
          </>
        )}
      </section>
      <HomeFooter />
    </>
  );
}

export default OrgPage;
